import { Box, Typography, Button, Divider } from '@mui/material';

/**
 * Pretty-prints an XML string with indentation.
 * Throws if the string isn't valid XML.
 */
const formatXml = (xml) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, 'application/xml');

        // DOMParser doesn't throw on invalid XML; it embeds a <parsererror> node instead.
        const parseError = xmlDoc.getElementsByTagName('parsererror');
        if (parseError.length > 0) {
                throw new Error('Invalid XML');
        }

        const serializer = new XMLSerializer();
        const rawXml = serializer.serializeToString(xmlDoc);

        // Manual indentation since XMLSerializer doesn't format output.
        const PADDING = '  ';
        let formatted = '';
        let pad = 0;

        rawXml
                .replace(/(>)(<)(\/*)/g, '$1\n$2$3') // add newlines between tags
                .split('\n')
                .forEach((node) => {
                        let indent = 0;
                        if (/^<\/\w/.test(node)) {
                                // closing tag
                                pad = Math.max(pad - 1, 0);
                        } else if (/^<\w[^>]*[^/]>.*$/.test(node) && !/^<\?/.test(node)) {
                                // opening tag (not self-closing, not declaration)
                                indent = 1;
                        }

                        formatted += PADDING.repeat(pad) + node + '\n';
                        pad += indent;
                });

        return formatted.trim();
};

/**
 * Detects a structured ISO 8583 payload (JSON with an MTI + field map) and
 * renders it as a readable list. Makes no assumptions about field names —
 * just labels each entry by its data element number (DEnnn).
 *
 * Expected shapes handled:
 *   { mti: "0200", fields: { "2": "...", "3": "...", ... } }
 *   { mti: "0200", "2": "...", "3": "...", ... }
 *
 * Throws if the payload doesn't look like an ISO 8583 message, so callers
 * can fall back to plain JSON formatting.
 */
const formatIso8583 = (raw) => {
        const parsed = JSON.parse(raw);

        const mti = parsed.mti ?? parsed.MTI;
        const fieldMap = parsed.fields ?? parsed.Fields ?? parsed;

        if (!mti || typeof fieldMap !== 'object' || fieldMap === null) {
                throw new Error('Not an ISO 8583 message');
        }

        const fieldEntries = Object.entries(fieldMap)
                .filter(([key]) => /^\d+$/.test(key)) // only numeric DE keys
                .sort(([a], [b]) => Number(a) - Number(b));

        if (fieldEntries.length === 0) {
                throw new Error('Not an ISO 8583 message');
        }

        const lines = [`MTI: ${mti}`, ''];
        fieldEntries.forEach(([num, value]) => {
                const paddedNum = String(num).padStart(3, '0');
                lines.push(`DE${paddedNum}: ${typeof value === 'object' ? JSON.stringify(value) : value}`);
        });

        return lines.join('\n');
};

/**
 * Detects the format of a string payload and returns a pretty-printed version.
 * Detection order: XML -> ISO 8583 (structured JSON) -> plain JSON -> raw text fallback.
 */
const beautify = (raw) => {
        const trimmed = raw.trim();

        if (trimmed.startsWith('<')) {
                try {
                        return formatXml(trimmed);
                } catch (e) {
                        // fall through to plain text below
                }
        } else if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                        return formatIso8583(trimmed);
                } catch (e) {
                        // not ISO 8583 shape, try plain JSON
                }
                try {
                        return JSON.stringify(JSON.parse(trimmed), null, 2);
                } catch (e) {
                        // fall through to plain text below
                }
        }

        return raw;
};

/**
 * Renders raw endpoint log data inside MDForm's CustomModal.
 * Shows the data pretty-printed as JSON, XML, or ISO 8583 when parseable,
 * otherwise as plain text.
 */
const EndpointLogModal = ({
        data,
        okLabel = 'OK',
        onOk,
}) => {
        const handleOk = () => {
                if (onOk) {
                        onOk();
                }
        };

        let display = '';
        if (data != null && data !== '') {
                display = beautify(String(data));
        }

        return (
                <Box id="endpointLogMdlFrgmtId" sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box className="modal-body" sx={{ py: 2 }}>
                                {display ? (
                                        <Box
                                                component="pre"
                                                sx={{
                                                        m: 0,
                                                        maxHeight: '60vh',
                                                        overflow: 'auto',
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.8125rem',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                }}
                                        >
                                                {display}
                                        </Box>
                                ) : (
                                        <Typography
                                                component="h6"
                                                variant="subtitle1"
                                                sx={{ color: '#828080', fontWeight: 400, m: 0 }}
                                        >
                                                No screening data available.
                                        </Typography>
                                )}
                        </Box>
                        <Divider />
                        <Box className="modal-footer" sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <Button
                                        type="button"
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        onClick={handleOk}
                                >
                                        {okLabel}
                                </Button>
                        </Box>
                </Box>
        );
};

export default EndpointLogModal;